import { loggerLink, httpBatchLink, type TRPCClientError } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { type inferRouterInputs, type inferRouterOutputs } from "@trpc/server";
import SuperJSON from "superjson";
import {
  isProductionEnvironment,
  isPreviewEnvironment,
} from "@/utils/envUtils";
import { env } from "@/env";
import * as Sentry from "@sentry/browser";

import { type AppRouter } from "@/server/api/root";
import { transformTrpcUrl } from "@/shared/utils/urlTransform";

export const api = createTRPCReact<AppRouter>();

export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

function createCustomLoggerLink() {
  return loggerLink({
    enabled: (op) =>
      !isProductionEnvironment() ||
      (op.direction === "down" && op.result instanceof Error),
    colorMode: "none",
    console: {
      log: (...args: unknown[]) => {
        console.log(...args);
      },
      error: (
        text: string,
        error: { input: unknown; result: TRPCClientError<AppRouter> },
      ) => {
        if (isProductionEnvironment() || isPreviewEnvironment()) {
          Sentry.captureException(error.result.message, {
            fingerprint: error.result.data
              ? [
                  error.result.data?.path ?? "",
                  error.result.data?.code ?? "",
                  String(error.result.data?.httpStatus),
                  error.result.data?.stack ?? "",
                ]
              : [
                  error.result.message,
                  error.result.name,
                  error.result.stack ?? "",
                ],
            tags: error.result.data
              ? {
                  path: error.result.data?.path ?? "",
                  code: error.result.data?.code ?? "",
                  httpStatus: String(error.result.data?.httpStatus),
                  stack: error.result.data?.stack ?? "",
                }
              : {
                  message: error.result.message,
                  name: error.result.name,
                  stack: error.result.stack,
                },
            extra: {
              error: JSON.stringify(error, null, 2),
            },
            // @ts-expect-error -- Sentry types are not up to date
            attachments: [
              {
                filename: "errorFull.json",
                data: JSON.stringify(error, null, 2),
                contentType: "application/json",
                attachmentType: "event.attachment",
              },
            ],
          });
        }
        console.error(text, error);
      },
    },
  });
}

export function createTRPCLinks(source: string) {
  return [
    createCustomLoggerLink(),
    httpBatchLink({
      fetch: async (url, options) => {
        const transformedUrl = transformTrpcUrl(url, getBaseUrl());

        return fetch(transformedUrl, {
          ...options,
          credentials: "include",
        } as RequestInit);
      },
      transformer: SuperJSON,
      url: getBaseUrl(),
      headers: () => {
        const headers = new Headers();
        headers.set("x-trpc-source", source);

        // Add preview access header if in preview environment
        if (isPreviewEnvironment()) {
          const previewSecret =
            typeof window !== "undefined"
              ? localStorage.getItem("preview_access_server_secret")
              : env.SUPER_USER_SECRET || env.VITE_SUPER_USER_SECRET;

          if (previewSecret) {
            headers.set("X-Preview-Access", previewSecret);
          }
        }

        return headers;
      },
    }),
  ];
}

export function getBaseUrl() {
  if (typeof window !== "undefined") {
    return env.VITE_BE_URL ?? window.location.origin;
  }
  return env.VITE_BE_URL ?? "";
}
